import React, { useState, useEffect } from 'react'
import { X, Download } from 'lucide-react'
import ModalTest from '../../../BaseModal'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { ContainerOfModal, Header, HeaderTitle, CloseDiv, Button } from '../../../styles/modal-style'
import { ProductData } from '../../../../pages/ClientPanel/ProductComposition/RegisterComposition'
import TechnicalSheetPDF from '../../../../components/PDF/FashionProduct/TechnicalSheetPDF'
import {
  SwitchesContainer,
  SwitchRow,
  SwitchLabelText,
  SwitchBox,
  SwitchInput,
  SwitchSlider
} from './style'
import { ApprovalApi } from '../../../../pages/ClientPanel/ProductComposition/ApprovationModal/api'
import axios from 'axios'

export interface SelectedSections {
  imagens: boolean
  cores: boolean
  composicao: boolean
  anotacoes: boolean
  aprovacoes: boolean
  medidas: boolean
}

interface GeneratePDFModalProps {
  onClickClose: () => void
  productData: ProductData | null
  gradesByType: any
  gradeTypes: any[]
  coverImageUrl?: string | null
  attachmentImageUrls?: string[]
  extraData?: any
  compositionItems?: any[]
  liveObservations?: string
  liveAnotacoes?: any[]
  liveCores?: any[]
  liveMedidas?: number[]
}

const GeneratePDFModal: React.FC<GeneratePDFModalProps> = ({ onClickClose, productData, gradesByType, gradeTypes, coverImageUrl, attachmentImageUrls, extraData, compositionItems, liveObservations, liveAnotacoes, liveCores, liveMedidas }) => {
  const [selectedSections, setSelectedSections] = useState<SelectedSections>({
    imagens: false,
    cores: false,
    composicao: false,
    anotacoes: false,
    aprovacoes: false,
    medidas: false
  })
  
  const [base64Cover, setBase64Cover] = useState<string | null>(null)
  const [base64Images, setBase64Images] = useState<string[]>([])
  const [isConvertingImages, setIsConvertingImages] = useState(false)

  const attachmentImageUrlsKey = (attachmentImageUrls || []).join('|')

  useEffect(() => {
    const convertImageUrlToDataUrl = async (imageUrl: string): Promise<string | null> => {
      if (imageUrl.startsWith('data:')) return imageUrl
      if (imageUrl.startsWith('blob:')) {
        try {
          const blob = await fetch(imageUrl).then(res => res.blob())
          return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        } catch (err) {
          console.error('Erro ao converter imagem local para base64:', err)
          return null
        }
      }
      try {
        const token = localStorage.getItem('token')
        const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
        const response = await axios.get(`${GATEWAY_URL}/backend-fashion-orders/fashion-product/product-attachments/base64`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { filePath: imageUrl }
        })
        const dataUrl = response.data?.dataUrl
        return typeof dataUrl === 'string' && dataUrl.startsWith('data:') ? dataUrl : null
      } catch (err) {
        console.error('Erro ao converter imagem do produto para base64:', imageUrl, err)
        return null
      }
    }

    const orderedUrls = [coverImageUrl, ...(attachmentImageUrls || [])]
      .filter((url): url is string => Boolean(url))
    const uniqueUrls = Array.from(new Set(orderedUrls))
    if (!uniqueUrls.length) return

    let cancelled = false
    setIsConvertingImages(true)
    Promise.all(uniqueUrls.map(convertImageUrlToDataUrl)).then(results => {
      if (cancelled) return
      const convertedImages = results.filter((result): result is string => Boolean(result))
      setBase64Images(convertedImages)
      if (convertedImages.length > 0) setBase64Cover(convertedImages[0])
      setIsConvertingImages(false)
    })

    return () => {
      cancelled = true
    }
  }, [coverImageUrl, attachmentImageUrlsKey])

  const [liveAprovacoes, setLiveAprovacoes] = useState<any[]>([])
  const [liveMeasures, setLiveMeasures] = useState<any[]>([])

  useEffect(() => {
    if (productData?.id) {
      ApprovalApi.list(Number(productData.id))
        .then(res => setLiveAprovacoes(res))
        .catch(err => console.error('Erro ao buscar aprovações no modal de PDF:', err))
        
      const token = localStorage.getItem('token')
      const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
      axios.get(`${GATEWAY_URL}/backend-fashion-orders/fashion-product/${productData.id}/measures`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setLiveMeasures(Array.isArray(res.data) ? res.data : [])
      })
      .catch(err => console.error('Erro ao buscar medidas no modal de PDF:', err))
    }
  }, [productData?.id])

  const toggleSection = (key: keyof SelectedSections) => {
    setSelectedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const hasImagens = !!coverImageUrl || (attachmentImageUrls || []).length > 0;
  const hasCores = !!(liveCores && liveCores.length > 0) || !!(productData?.productItemColors && productData.productItemColors.length > 0);
  const hasComposicao = !!(compositionItems && compositionItems.length > 0) || !!(productData?.composition?.variations && productData.composition.variations.length > 0);
  const hasAnotacoes = !!liveObservations || !!(liveAnotacoes && liveAnotacoes.length > 0) || !!productData?.observations;
  const hasMedidas = liveMeasures.length > 0;
  const hasAprovacoes = !!(liveAprovacoes && liveAprovacoes.length > 0);

  return (
    <ModalTest>
      <ContainerOfModal width='450px'>
        <Header>
          <HeaderTitle>Gerar PDF da Ficha Técnica</HeaderTitle>
          <CloseDiv onClick={onClickClose}>
            <X size={20} />
          </CloseDiv>
        </Header>
        
        <div>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '1rem' }}>
            Selecione as seções que deseja incluir no documento PDF:
          </p>

          <SwitchesContainer>
            <SwitchRow style={{ opacity: hasImagens ? 1 : 0.5, cursor: hasImagens ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Imagens</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasImagens && selectedSections.imagens} 
                  onChange={() => hasImagens && toggleSection('imagens')} 
                  disabled={!hasImagens}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasCores ? 1 : 0.5, cursor: hasCores ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Cores</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasCores && selectedSections.cores} 
                  onChange={() => hasCores && toggleSection('cores')} 
                  disabled={!hasCores}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasComposicao ? 1 : 0.5, cursor: hasComposicao ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Composição</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasComposicao && selectedSections.composicao} 
                  onChange={() => hasComposicao && toggleSection('composicao')} 
                  disabled={!hasComposicao}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasAnotacoes ? 1 : 0.5, cursor: hasAnotacoes ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Anotações</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasAnotacoes && selectedSections.anotacoes} 
                  onChange={() => hasAnotacoes && toggleSection('anotacoes')} 
                  disabled={!hasAnotacoes}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasAprovacoes ? 1 : 0.5, cursor: hasAprovacoes ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Aprovações</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasAprovacoes && selectedSections.aprovacoes} 
                  onChange={() => hasAprovacoes && toggleSection('aprovacoes')} 
                  disabled={!hasAprovacoes}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>

            <SwitchRow style={{ opacity: hasMedidas ? 1 : 0.5, cursor: hasMedidas ? 'pointer' : 'not-allowed' }}>
              <SwitchLabelText style={{ cursor: 'inherit' }}>Medidas</SwitchLabelText>
              <SwitchBox style={{ cursor: 'inherit' }}>
                <SwitchInput 
                  type="checkbox" 
                  checked={hasMedidas && selectedSections.medidas} 
                  onChange={() => hasMedidas && toggleSection('medidas')} 
                  disabled={!hasMedidas}
                />
                <SwitchSlider className="slider" />
              </SwitchBox>
            </SwitchRow>
          </SwitchesContainer>
          
          <div style={{ marginTop: '2rem' }}>
            {productData ? (
              <PDFDownloadLink
                document={<TechnicalSheetPDF data={productData} sections={selectedSections} gradesByType={gradesByType} gradeTypes={gradeTypes} coverImageUrl={base64Cover} attachmentImages={base64Images} extraData={extraData} compositionItems={compositionItems} liveObservations={liveObservations} liveAnotacoes={liveAnotacoes} liveAprovacoes={liveAprovacoes} liveCores={liveCores} liveMedidas={liveMedidas} />}
                fileName={`Ficha_Tecnica_${productData.productCode || 'Produto'}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                {({ loading }) => (
                  <Button 
                    disabled={loading || isConvertingImages} 
                    onClick={() => {
                      if (!loading && !isConvertingImages) {
                        setTimeout(() => onClickClose(), 800)
                      }
                    }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <Download size={18} style={{ marginRight: '8px' }} />
                    {isConvertingImages ? 'Preparando imagens...' : loading ? 'Gerando documento...' : 'Gerar e Baixar PDF'}
                  </Button>
                )}
              </PDFDownloadLink>
            ) : (
              <Button disabled style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Download size={18} style={{ marginRight: '8px' }} />
                Carregando dados...
              </Button>
            )}
          </div>
        </div>
      </ContainerOfModal>
    </ModalTest>
  )
}

export default GeneratePDFModal
